import React from "react";
import { Badge, Button, Callout, Card, Checkbox, Field, Input, Select } from "../components/DesignSystem";
import { createEmptyOnboardingDraft } from "../domain/defaults";
import { FULL_PLANNER_MODULES, moduleLabel } from "../domain/modules";
import type { AuthSession, EventInput, OnboardingDraft, PlannerModuleDefinition, PlannerModuleKey } from "../domain/types";
import { validateOnboardingStep } from "../domain/validation";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";

const STEPS = ["Access", "Couple", "Where & when", "Celebrations", "Modules", "Review"];
const SUGGESTED_EVENTS = ["Mehndi", "Nikkah", "Baraat", "Walima", "Haldi", "Sangeet", "Ceremony", "Reception", "Welcome Dinner", "Rehearsal Dinner", "Brunch", "Tea Ceremony"];

export function OnboardingScreen({ onComplete }: { onComplete: (session: AuthSession | null) => void }) {
  const { service, setWorkspace, session, setSession } = usePlannerData();
  const [step, setStep] = React.useState(0);
  const [draft, setDraft] = React.useState<OnboardingDraft>(() => createEmptyOnboardingDraft(session));
  const [modules, setModules] = React.useState<PlannerModuleDefinition[]>([]);
  const [pendingModule, setPendingModule] = React.useState<PlannerModuleKey | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const validation = validateOnboardingStep(step, draft);

  const update = (patch: Partial<OnboardingDraft>) => setDraft((current) => ({ ...current, ...patch }));
  const moduleKeys = modules.length ? modules.map((module) => module.key) : FULL_PLANNER_MODULES;
  const labelFor = (key: PlannerModuleKey) => modules.find((module) => module.key === key)?.label || moduleLabel(key);
  const missingFor = (selected: PlannerModuleKey[], key: PlannerModuleKey) => (modules.find((module) => module.key === key)?.dependencies || []).filter((dependency) => !selected.includes(dependency));
  const withDepsFor = (selected: PlannerModuleKey[], key: PlannerModuleKey) => {
    const out = new Set<PlannerModuleKey>(selected);
    const visit = (moduleKey: PlannerModuleKey) => {
      if (out.has(moduleKey)) return;
      const module = modules.find((item) => item.key === moduleKey);
      module?.dependencies.forEach(visit);
      out.add(moduleKey);
    };
    visit(key);
    return Array.from(out);
  };

  React.useEffect(() => {
    let cancelled = false;
    service.modules.listModules().then((catalog) => {
      if (cancelled) return;
      setModules(catalog);
      setDraft((current) => current.planTier === "full" ? { ...current, selectedModules: catalog.map((module) => module.key) } : current);
    }).catch((err) => {
      if (!cancelled) setError(errorMessage(err));
    });
    return () => {
      cancelled = true;
    };
  }, [service]);

  const choosePlan = (planTier: "full" | "custom") => {
    update({ planTier, selectedModules: planTier === "full" ? moduleKeys : ["events", "guests", "households", "rsvp"] });
  };

  // Switching away from Full Planner from the Modules step itself (rather than the Access step)
  // means the owner has already seen every module enabled and wants to trim some off -- so this
  // keeps the current (full) selection as the Custom starting point instead of resetting to the
  // small default set choosePlan uses for a first-time pick. Every module stays enabled until the
  // owner deselects one, and existing dependency-closure logic in toggleModule still applies.
  const switchToCustomKeepingSelection = () => update({ planTier: "custom" });

  const toggleModule = (key: PlannerModuleKey) => {
    if (draft.planTier === "full") return;
    if (draft.selectedModules.includes(key)) {
      update({ selectedModules: draft.selectedModules.filter((item) => item !== key) });
      return;
    }
    const missing = missingFor(draft.selectedModules, key);
    if (missing.length > 0) {
      setPendingModule(key);
      return;
    }
    update({ selectedModules: [...draft.selectedModules, key] });
  };

  const acceptDependencies = () => {
    if (!pendingModule) return;
    update({ selectedModules: withDepsFor(draft.selectedModules, pendingModule) });
    setPendingModule(null);
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const workspace = await service.weddings.completeOnboarding(draft);
      setWorkspace(workspace);
      const nextSession = await service.auth.getSession();
      if (nextSession) setSession(nextSession);
      onComplete(nextSession);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pob-app" style={{ display: "grid", gridTemplateColumns: "300px 1fr", height: "100%", overflow: "hidden" }}>
      <aside style={{ padding: "var(--space-6)", borderRight: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
        <span className="pob-eyebrow">Setup</span>
        <h1 style={{ font: "var(--type-section)" }}>Start planning</h1>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {STEPS.map((label, index) => (
            <button key={label} type="button" onClick={() => setStep(index)} style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
              height: 34,
              border: 0,
              borderRadius: "var(--radius-md)",
              background: step === index ? "var(--surface-card)" : "transparent",
              color: step === index ? "var(--text-primary)" : "var(--text-secondary)",
              cursor: "pointer",
              textAlign: "left",
              padding: "0 var(--space-3)",
              font: "var(--type-label)"
            }}>
              <span className="pob-tnum">{index + 1}</span>
              {label}
            </button>
          ))}
        </div>
        <Callout tone="neutral" title={draft.planTier === "full" ? "Full Planner" : "Custom Planner"}>
          {draft.selectedModules.length} modules selected.
        </Callout>
      </aside>
      <main style={{ overflowY: "auto" }}>
        <div className="pob-pad" style={{ maxWidth: "var(--prose-max)" }}>
          {error && <Callout tone="error" title="Onboarding could not be saved">{error}</Callout>}
          {step === 0 && <AccessStep draft={draft} choosePlan={choosePlan} />}
          {step === 1 && <CoupleStep draft={draft} update={update} />}
          {step === 2 && <WhereWhenStep draft={draft} update={update} />}
          {step === 3 && <CelebrationsStep draft={draft} update={update} />}
          {step === 4 && <ModulesStep draft={draft} modules={modules} toggleModule={toggleModule} onSwitchToCustom={switchToCustomKeepingSelection} />}
          {step === 5 && <ReviewStep draft={draft} />}
          {pendingModule && (
            <Callout tone="warning" title={`${labelFor(pendingModule)} needs related modules`} action={<div style={{ display: "flex", gap: "var(--space-2)" }}><Button size="sm" onClick={() => setPendingModule(null)}>Cancel</Button><Button size="sm" variant="primary" onClick={acceptDependencies}>Include dependencies</Button></div>}>
              Add {missingFor(draft.selectedModules, pendingModule).map(labelFor).join(", ")} before enabling {labelFor(pendingModule)}.
            </Callout>
          )}
          {!validation.valid && <Callout tone="error" title="Complete this step">{Object.values(validation.errors)[0]}</Callout>}
          <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-2)" }}>
            <Button disabled={step === 0 || saving} onClick={() => setStep(step - 1)}>Back</Button>
            {step < STEPS.length - 1 ? (
              <Button variant="primary" disabled={!validation.valid || saving} onClick={() => setStep(step + 1)}>Continue</Button>
            ) : (
              <Button variant="primary" disabled={!validation.valid || saving} onClick={submit}>{saving ? "Saving..." : "Open the dashboard"}</Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function AccessStep({ draft, choosePlan }: { draft: OnboardingDraft; choosePlan: (tier: "full" | "custom") => void }) {
  return (
    <>
      <Header eyebrow="Step 1" title="Select access" />
      <div className="pob-grid-2">
        <PlanCard active={draft.planTier === "full"} title="Full Planner" meta="Every approved planner module enabled" onClick={() => choosePlan("full")} />
        <PlanCard active={draft.planTier === "custom"} title="Custom Planner" meta="Choose planning modules and review dependencies" onClick={() => choosePlan("custom")} />
      </div>
    </>
  );
}

function CoupleStep({ draft, update }: { draft: OnboardingDraft; update: (patch: Partial<OnboardingDraft>) => void }) {
  return (
    <>
      <Header eyebrow="Step 2" title="The couple" />
      <div className="pob-grid-2">
        <Field label="Your name"><Input value={draft.personName} onChange={(e) => update({ personName: e.target.value })} /></Field>
        <Field label="Partner's name"><Input value={draft.partnerName} onChange={(e) => update({ partnerName: e.target.value })} /></Field>
        <Field label="Wedding title"><Input value={draft.title} onChange={(e) => update({ title: e.target.value })} /></Field>
        <Field label="Phone optional"><Input value={draft.phone || ""} onChange={(e) => update({ phone: e.target.value })} /></Field>
      </div>
    </>
  );
}

// A Select whose bound value doesn't match any of its options falls back to silently displaying
// the first option in the DOM while the real (controlled) value stays whatever it actually is --
// that's the reproduced bug where Country visually showed "Pakistan" while validation still said
// "Choose a country." A placeholder option closes the gap for a genuinely empty value; for
// Timezone, the browser-resolved default can itself be a zone outside our short list, so it's
// added to the options rather than masked -- either way, whatever is shown on screen is the real
// value, never a fallback DOM default standing in for one that doesn't match.
const BASE_TIMEZONE_OPTIONS = ["Asia/Karachi", "Asia/Dubai", "Europe/London", "America/New_York"];

function WhereWhenStep({ draft, update }: { draft: OnboardingDraft; update: (patch: Partial<OnboardingDraft>) => void }) {
  const timezoneOptions = draft.timezone && !BASE_TIMEZONE_OPTIONS.includes(draft.timezone) ? [draft.timezone, ...BASE_TIMEZONE_OPTIONS] : BASE_TIMEZONE_OPTIONS;
  return (
    <>
      <Header eyebrow="Step 3" title="Where and when" />
      <div className="pob-grid-2">
        <Field label="Country"><Select placeholder="Select a country" value={draft.country} onChange={(e) => update({ country: e.target.value })} options={["Pakistan", "India", "United Kingdom", "United Arab Emirates", "United States"]} /></Field>
        <Field label="City"><Input value={draft.city} onChange={(e) => update({ city: e.target.value })} /></Field>
        <Field label="First celebration"><Input type="date" value={draft.startDate} onChange={(e) => update({ startDate: e.target.value })} /></Field>
        <Field label="Last celebration"><Input type="date" value={draft.endDate} onChange={(e) => update({ endDate: e.target.value })} /></Field>
        <Field label="Timezone"><Select placeholder={draft.timezone ? undefined : "Select a timezone"} value={draft.timezone} onChange={(e) => update({ timezone: e.target.value })} options={timezoneOptions} /></Field>
        <Field label="Approximate guests"><Input type="number" value={draft.approximateGuests} onChange={(e) => update({ approximateGuests: Number(e.target.value) })} /></Field>
      </div>
    </>
  );
}

// Every new celebration used to default to the exact same day (startDate), which meant a
// 5-celebration wedding spanning a real date range needed every date fixed by hand except the
// first. This spreads each newly added celebration one day later than the last, in whatever
// order the owner picks them (never a hardcoded Mehndi-before-Nikkah-before-Baraat assumption),
// clamped so it never runs past endDate. Every date stays freely editable/reorderable per row.
export function nextCelebrationDate(startDate: string, endDate: string | undefined, alreadySelectedCount: number): string {
  if (!startDate) return startDate;
  // Date arithmetic done entirely in UTC millis (Date.UTC in, toISOString out) -- parsing
  // "YYYY-MM-DDT00:00:00" as local time and converting back with toISOString can land on the
  // wrong calendar day depending on the browser/server's timezone offset.
  const [sy, sm, sd] = startDate.split("-").map(Number);
  const startUtc = Date.UTC(sy, sm - 1, sd);
  if (!endDate) return startDate;
  const [ey, em, ed] = endDate.split("-").map(Number);
  const endUtc = Date.UTC(ey, em - 1, ed);
  if (endUtc <= startUtc) return startDate;
  const totalDays = Math.round((endUtc - startUtc) / 86400000);
  const offset = Math.min(alreadySelectedCount, totalDays);
  return new Date(startUtc + offset * 86400000).toISOString().slice(0, 10);
}

function CelebrationsStep({ draft, update }: { draft: OnboardingDraft; update: (patch: Partial<OnboardingDraft>) => void }) {
  const selected = new Set(draft.events.map((event) => event.name));
  const toggle = (name: string) => {
    if (selected.has(name)) {
      update({ events: draft.events.filter((event) => event.name !== name) });
      return;
    }
    const event: EventInput = { name, type: name, date: nextCelebrationDate(draft.startDate, draft.endDate, draft.events.length), time: "18:00", venue: draft.city, status: "planned", custom: !SUGGESTED_EVENTS.includes(name) };
    update({ events: [...draft.events, event] });
  };
  const addCustom = () => {
    const name = `Custom event ${draft.events.filter((event) => event.custom).length + 1}`;
    update({ events: [...draft.events, { name, type: "Custom", date: nextCelebrationDate(draft.startDate, draft.endDate, draft.events.length), time: "18:00", venue: draft.city, custom: true, status: "planned" }] });
  };
  return (
    <>
      <Header eyebrow="Step 4" title="Celebrations" />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
        {SUGGESTED_EVENTS.map((name) => <Button key={name} variant={selected.has(name) ? "primary" : "secondary"} onClick={() => toggle(name)}>{name}</Button>)}
        <Button icon="plus" onClick={addCustom}>Custom event</Button>
      </div>
      <Card eyebrow="Selected" title={`${draft.events.length} celebrations`} padding="0">
        {draft.events.map((event, index) => (
          <div key={`${event.name}-${index}`} style={{ display: "grid", gridTemplateColumns: "1fr 160px 140px", gap: "var(--space-3)", alignItems: "center", padding: "var(--space-3) var(--space-4)", borderTop: index ? "1px solid var(--divider)" : 0 }}>
            <Input value={event.name} onChange={(e) => update({ events: draft.events.map((item, i) => i === index ? { ...item, name: e.target.value, custom: true } : item) })} />
            <Input type="date" value={event.date} onChange={(e) => update({ events: draft.events.map((item, i) => i === index ? { ...item, date: e.target.value } : item) })} />
            <Button variant="ghost" onClick={() => update({ events: draft.events.filter((_, i) => i !== index) })}>Remove</Button>
          </div>
        ))}
      </Card>
    </>
  );
}

function ModulesStep({ draft, modules, toggleModule, onSwitchToCustom }: { draft: OnboardingDraft; modules: PlannerModuleDefinition[]; toggleModule: (key: PlannerModuleKey) => void; onSwitchToCustom: () => void }) {
  const catalog = modules.length ? modules : [];
  const groups = Array.from(new Set(catalog.map((module) => module.group)));
  return (
    <>
      <Header eyebrow="Step 5" title={draft.planTier === "full" ? "Full Planner modules" : "Custom Planner modules"} />
      {draft.planTier === "full" && (
        <Callout tone="success" title="Full Planner includes every module" action={<Button size="sm" onClick={onSwitchToCustom}>Switch to Custom Planner</Button>}>
          Every approved module is enabled and nothing here needs picking. If you'd rather choose which modules to use, switch to Custom Planner -- your current selection carries over and you can turn individual modules off.
        </Callout>
      )}
      {catalog.length === 0 && <Callout tone="warning" title="Module catalog unavailable">The planner cannot save custom entitlements until the module catalog loads from the service.</Callout>}
      {groups.map((group) => (
        <Card key={group} eyebrow={group} padding="var(--space-4)">
          <div className="pob-grid-2">
            {catalog.filter((module) => module.group === group).map((module) => {
              const checked = draft.selectedModules.includes(module.key);
              return <Checkbox key={module.key} checked={checked} disabled={draft.planTier === "full"} label={module.label} description={module.dependencyExplanation || (module.dependencies.length ? `Depends on ${module.dependencies.map(moduleLabel).join(", ")}` : "Standalone module")} onChange={() => toggleModule(module.key)} />;
            })}
          </div>
        </Card>
      ))}
    </>
  );
}

function ReviewStep({ draft }: { draft: OnboardingDraft }) {
  return (
    <>
      <Header eyebrow="Ready" title="Review setup" />
      <Card padding="var(--space-5)">
        <div className="pob-grid-2">
          <Summary label="Planner" value={draft.planTier === "full" ? "Full Planner" : "Custom Planner"} />
          <Summary label="Modules" value={draft.selectedModules.length} />
          <Summary label="Couple" value={draft.title} />
          <Summary label="Location" value={`${draft.city}, ${draft.country}`} />
          <Summary label="Celebrations" value={draft.events.length} />
          <Summary label="Approx. guests" value={draft.approximateGuests} />
        </div>
      </Card>
    </>
  );
}

function PlanCard({ active, title, meta, onClick }: { active: boolean; title: string; meta: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ textAlign: "left", border: `1px solid ${active ? "var(--border-accent)" : "var(--border-subtle)"}`, borderRadius: "var(--radius-lg)", background: active ? "var(--aubergine-50)" : "var(--surface-card)", padding: "var(--space-5)", cursor: "pointer", boxShadow: "var(--shadow-raised)" }}>
      <span style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)" }}>
        <span style={{ font: "var(--type-label)" }}>{title}</span>
        {active && <Badge tone="accent">Selected</Badge>}
      </span>
      <span style={{ display: "block", marginTop: "var(--space-2)", font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>{meta}</span>
    </button>
  );
}

function Header({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <span className="pob-eyebrow">{eyebrow}</span>
      <h2 style={{ font: "var(--type-title)", color: "var(--text-primary)" }}>{title}</h2>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: React.ReactNode }) {
  return <div><span className="pob-eyebrow">{label}</span><div style={{ marginTop: 4, font: "var(--type-label)" }}>{value}</div></div>;
}
